import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Exception } from '@cargos/sprintpay-models';
import { SubscribeToAWBParams } from '@cargos/sprintpay-services';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import { finalize, take } from 'rxjs';
import { InvalidFieldComponent, LogoFacilityComponent } from 'src/app/shared/components';
import { DisableControlDirective } from 'src/app/shared/directives/disable-control.directive';
import { MatStrokedButtonBorderDirective } from 'src/app/shared/directives/mat-stroked-button-border.directive';
import { SanitizerPipe } from 'src/app/shared/pipes';
import { CurrentFacilityService, SubscriptionAWBService, SummaryService } from 'src/app/shared/services';
import Swal from 'sweetalert2';
import { AwbSubscriptionService } from '../services/awb-subscription.service';
import { GuestSubscriptionFormErrorJSON, GuestSubscriptionFormGroupType } from './models/guest-subscription-form';
import { ModalDataType } from './models/types';
import { formGuestSubscriptionErrors } from './utils/form-errors';

@Component({
    selector: 'app-guest-subscription',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        LogoFacilityComponent,
        SanitizerPipe,
        MatStrokedButtonBorderDirective,
        DisableControlDirective,
        MatProgressSpinnerModule,
        InvalidFieldComponent,
    ],
    templateUrl: './guest-subscription.component.html',
    styleUrl: './guest-subscription.component.scss',
})
export class GuestSubscriptionComponent implements OnInit, AfterViewInit {
    public facilityEmail: string;
    public facilityName: string;
    public guestSubscriptionForm: GuestSubscriptionFormGroupType;
    public isSubscriptionEnableWithoutRCForNFD = false;
    public subscribing = false;
    public formErrors: GuestSubscriptionFormErrorJSON;

    get awb(): AbstractControl<string | null> | null {
        return this.guestSubscriptionForm.get('awb');
    }

    get email(): AbstractControl<string | null> | null {
        return this.guestSubscriptionForm.get('email');
    }

    constructor(
        private fb: FormBuilder,
        private currentFacilityService: CurrentFacilityService,
        private subscriptionAWBService: SubscriptionAWBService,
        private router: Router,
        private summaryService: SummaryService,
        @Inject(MAT_DIALOG_DATA) public data: ModalDataType,
        private matDialogRef: MatDialogRef<GuestSubscriptionComponent>,
        private awbSubscriptionService: AwbSubscriptionService
    ) {
        this.facilityName = this.currentFacilityService.getFacilityName();
        this.facilityEmail = this.currentFacilityService.getFacilityEmail();
        this.isSubscriptionEnableWithoutRCForNFD =
            this.awbSubscriptionService.getIsEnabledSubscriptionWithoutRCForNFD();
        this.formErrors = formGuestSubscriptionErrors;
    }

    ngOnInit(): void {
        this.createForm();
    }

    ngAfterViewInit(): void {
        if (this.data.awb) {
            setTimeout(() => {
                this.guestSubscriptionForm.get('awb')?.setValue(this.data.awb);
            });
        }
    }

    createForm(): void {
        this.guestSubscriptionForm = this.fb.group({
            awb: ['', [Validators.required]],
            email: ['', [CustomValidators.regex(new RegExp(CustomPatterns.EMAIL))]],
        });
    }

    subscribeToAWB(): void {
        if (this.subscribing) {
            return;
        }

        if (this.email?.value && this.guestSubscriptionForm.valid) {
            this.subscribing = true;
            const facilitySource = this.currentFacilityService.getFacilitySource();
            const body: SubscribeToAWBParams = {
                awb: this.awb?.value || '',
                sourceName: facilitySource,
                paymentType: 'ISC',
                paidTo: '',
                isAutoPay: false,
                arrivalNotificationEmail: this.email.value,
            };

            this.subscriptionAWBService
                .subscribeToAWB(body)
                .pipe(
                    take(1),
                    finalize(() => {
                        this.subscribing = false;
                    })
                )
                .subscribe({
                    next: (response) => {
                        if (response.isAutoPaySubscriptionExist || response.isArrivalSubscriptionExist) {
                            Swal.fire({
                                text: response.message,
                                icon: 'error',
                            });
                        } else {
                            this.closeModal();
                            Swal.fire({
                                text: `You have successfully subscribed to receive an email when MAWB ${this.awb?.value} has arrived`,
                                icon: 'success',
                                showConfirmButton: true,
                                allowOutsideClick: false,
                            }).then(() => {
                                this.redirect(true);
                            });
                        }
                    },
                    error: (error: Exception) => {
                        Swal.fire({
                            title: error.title,
                            text: error.description,
                            icon: error.type,
                        });
                    },
                });
        }

        if (!this.email?.value) {
            this.closeModal();
            this.redirect(false);
        }
    }

    redirect(acceptSubscription: boolean): void {
        if (!this.isSubscriptionEnableWithoutRCForNFD && acceptSubscription) {
            return;
        }
        const location = this.summaryService.getLocation();

        this.router.navigate([`/search-awb/details`], {
            queryParams: {
                mawb: this.data.awb,
                location,
            },
        });
    }

    closeModal(): void {
        this.matDialogRef.close();
    }
}
